import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { filter, first, map, timeout } from 'rxjs/operators';
import { BooksService } from './books.service';
import { readAnnotationData } from '../components/pdf-viewer/models/annotation-data';

// Jorge confirma MEDIUMBLOB en custom_data y group_custom_data.
export const MAX_ANNOTATION_BYTES = 16777215;

function annotationBytes(value: string): number {
    let bytes = 0;
    for (let i = 0; i < value.length; i++) {
        const code = value.charCodeAt(i);
        if (code < 0x80) { bytes++; } else if (code < 0x800) { bytes += 2; } else if (code >= 0xd800 && code <= 0xdbff) {
            const next = value.charCodeAt(++i);
            if (!(next >= 0xdc00 && next <= 0xdfff)) { return -1; }
            bytes += 4;
        } else if (code >= 0xdc00 && code <= 0xdfff) { return -1; } else { bytes += 3; }
    }
    return bytes;
}

function canonicalJson(value: any): string {
    const normalize = (item: any): any => {
        if (Array.isArray(item)) { return item.map(normalize); }
        if (item && typeof item === 'object') {
            return Object.keys(item).sort().reduce((result, key) => { result[key] = normalize(item[key]); return result; }, {});
        }
        return item;
    };
    return JSON.stringify(normalize(value));
}

export interface ViewerSavePayload {
    isGroupMode: number;
    page_current: number;
    custom_data: string;
    book_index_id: number;
    book_id: number;
    chapter_id: number;
    group_id?: number;
    revision?: number;
}

// Contrato aditivo con Laravel: solo page_current y page_save_at del usuario; nunca anotaciones.
export interface ViewerProgressPayload {
    progress_only: 1;
    isGroupMode: 0;
    page_current: number;
    book_index_id: number;
    book_id: number;
    chapter_id: number;
}

export interface ViewerSaveState {
    pending: number;
    saving: boolean;
    error: string | null;
}

@Injectable({ providedIn: 'root' })
export class ViewerPersistenceService {
    private queue: ViewerSavePayload[] = [];
    private active: ViewerSavePayload | null = null;
    private request: Subscription | null = null;
    private session = 0;
    private sessionOpen = true;
    private blockedError: string | null = null;
    private conflict = false;
    get hasConflict(): boolean { return this.conflict; }
    // Identidad y contenido rechazados con 413: reenviarlos sin cambios solo repetiría el error.
    private tooLarge: string | null = null;
    get hasTooLarge(): boolean { return !!this.tooLarge; }
    // Cambios que no pueden guardarse sin intervención: se ofrece la descarga y se impide salir sin descartarlos.
    get needsLocalCopy(): boolean { return this.conflict || !!this.tooLarge; }
    get canRetry(): boolean {
        return !this.conflict && !(this.tooLarge && this.queue.some(item => this.signature(item) === this.tooLarge));
    }
    expiredSessionWithConflict = false;
    private forcedExitApproved = false;

    get canForceExit(): boolean { return this.forcedExitApproved; }
    private editors = new Map<() => void, () => boolean>();
    sessionWarning: string | null = null;
    private state = new BehaviorSubject<ViewerSaveState>({ pending: 0, saving: false, error: null });
    readonly state$ = this.state.asObservable();
    private sharedRevisions = new Map<string, number>();
    // Progreso de lectura: canal propio que no altera la cola, los errores ni los conflictos de anotaciones.
    private progressQueue: ViewerProgressPayload[] = [];
    private progressActive: ViewerProgressPayload | null = null;
    private progressRequest: Subscription | null = null;
    private progressSaved = new Map<string, number>();
    private progressLatest = new Map<string, number>();

    constructor(private booksService: BooksService) {
        // Se envía cuando no hay un guardado de anotaciones en curso, para que una petición
        // de anotaciones anterior (que también escribe page_current) no deje una página antigua.
        this.state$.subscribe(state => { if (!state.saving) { this.sendProgress(); } });
    }

    saveProgress(bookId: number, chapterId: number, page: number): boolean {
        if (!this.sessionOpen || ![bookId, chapterId, page].every(value => Number.isInteger(value) && value > 0)) { return false; }
        const payload: ViewerProgressPayload = { progress_only: 1, isGroupMode: 0, page_current: page,
            book_index_id: chapterId, book_id: bookId, chapter_id: chapterId };
        const key = this.progressKey(payload);
        this.progressLatest.set(key, page);
        const pending = this.progressQueue.findIndex(item => this.progressKey(item) === key);
        // Página ya enviada o confirmada para ese tema: no se repite la escritura.
        const known = this.progressActive && this.progressKey(this.progressActive) === key ?
            this.progressActive.page_current : this.progressSaved.get(key);
        if (page === known) {
            if (pending >= 0) { this.progressQueue.splice(pending, 1); }
        } else if (pending >= 0) {
            this.progressQueue[pending] = payload;
        } else {
            this.progressQueue.push(payload);
        }
        this.sendProgress();
        return true;
    }

    private progressKey(data: ViewerProgressPayload): string { return `${data.book_id}:${data.chapter_id}`; }

    // Un guardado personal de anotaciones también escribe page_current. Si llevaba una página anterior
    // (por ejemplo, un reintento tras seguir leyendo), se vuelve a enviar la última página pedida.
    private afterPersonalSave(saved: ViewerSavePayload) {
        const key = `${saved.book_id}:${saved.chapter_id}`;
        const latest = this.progressLatest.get(key);
        if (latest === undefined || latest === saved.page_current) { return; }
        this.progressSaved.set(key, saved.page_current);
        const payload: ViewerProgressPayload = { progress_only: 1, isGroupMode: 0, page_current: latest,
            book_index_id: saved.chapter_id, book_id: saved.book_id, chapter_id: saved.chapter_id };
        const pending = this.progressQueue.findIndex(item => this.progressKey(item) === key);
        if (pending >= 0) { this.progressQueue[pending] = payload; } else { this.progressQueue.push(payload); }
    }

    private sendProgress() {
        if (this.progressActive || this.active || !this.progressQueue.length) { return; }
        const payload = this.progressQueue.shift();
        const session = this.session;
        this.progressActive = payload;
        const done = (saved: boolean) => {
            if (session !== this.session) { return; }
            if (saved) { this.progressSaved.set(this.progressKey(payload), payload.page_current); }
            this.progressActive = null;
            this.progressRequest = null;
            this.sendProgress();
        };
        try {
            // Un backend sin progress_only responde 400 sin escribir: el progreso se descarta sin bloquear nada.
            const subscription = this.booksService.saveViewerConfig(payload).pipe(first(), timeout(30000)).subscribe(
                response => done(!!response && !response.error && response.message === 'Se ha guardado la configuracion.'),
                () => done(false));
            if (this.progressActive === payload) { this.progressRequest = subscription; } else { subscription.unsubscribe(); }
        } catch (_) { done(false); }
    }

    holdExpiredSession(): void { this.expiredSessionWithConflict = true; }
    approveForcedExit(): void { this.forcedExitApproved = true; }

    get hasPending(): boolean { return !!this.active || !!this.queue.length || !!this.error; }
    get error(): string | null { return this.state.value.error; }
    get saving(): boolean { return !!this.active; }

    registerEditor(prepare: () => void, dirty: () => boolean = () => false): () => void {
        this.editors.set(prepare, dirty);
        return () => this.editors.delete(prepare);
    }

    enqueue(payload: ViewerSavePayload): boolean {
        if (!this.sessionOpen) { return false; }
        if (readAnnotationData(payload.custom_data).error || typeof payload.custom_data !== 'string' ||
            payload.book_index_id !== payload.chapter_id || !Number.isInteger(payload.page_current) || payload.page_current < 1) {
            this.blockedError = 'No se ha guardado: la configuración del capítulo no es válida.';
            this.publish(null);
            return false;
        }
        // Capacidad por columna, sin inferir que el cambio de tipo explique la pérdida histórica.
        const bytes = annotationBytes(payload.custom_data);
        if (bytes < 0) {
            this.blockedError = 'No se ha guardado: las anotaciones contienen una codificación no válida.';
            this.publish(null);
            return false;
        }
        const capacity = MAX_ANNOTATION_BYTES;
        if (bytes > capacity) {
            this.blockedError = 'No se ha guardado: las anotaciones superan la capacidad configurada (' +
                capacity + ' bytes). Mantenga abierto el visor.';
            this.publish(null);
            return false;
        }
        const copy = { ...payload };
        const previousError = this.error === this.blockedError ? null : this.error;
        this.blockedError = null;
        const key = this.key(copy);
        const pending = this.queue.findIndex(item => this.key(item) === key);
        if (pending >= 0) { this.queue[pending] = copy; } else { this.queue.push(copy); }
        this.publish(previousError);
        this.sendNext();
        return true;
    }

    private key(data: ViewerSavePayload): string {
        return `${data.book_id}:${data.chapter_id}:${data.isGroupMode}:${data.group_id || 0}`;
    }

    private signature(data: ViewerSavePayload): string { return this.key(data) + '\n' + data.custom_data; }

    private publish(error: string | null) {
        this.state.next({ pending: this.queue.length + (this.active ? 1 : 0), saving: !!this.active,
            error: error || this.blockedError });
    }

    private sendNext() {
        if (this.active || this.error || !this.queue.length) { return; }
        const payload = this.queue.shift();
        const session = this.session;
        this.active = payload;
        this.publish(null);
        const failed = (error?: any) => {
            if (session !== this.session) { return; }
            this.active = null;
            this.request = null;
            if (!this.queue.some(item => this.key(item) === this.key(payload))) { this.queue.unshift(payload); }
            this.conflict = !!(payload.group_id && error && error.status === 409);
            this.tooLarge = error && error.status === 413 ? this.signature(payload) : null;
            this.publish(this.conflict ? 'Las anotaciones compartidas han cambiado. Los cambios locales se conservan; no se han guardado.' :
                this.tooLarge ? 'No se ha guardado: las anotaciones superan el tamaño que admite el servidor. Los cambios se ' +
                    'conservan en el visor; descárgalos o reduce las anotaciones antes de reintentar.' :
                'No se ha podido confirmar el guardado. Mantenga abierto el visor y pulse Reintentar.');
        };
        try {
            const operation = payload.group_id ? this.booksService.putGroupAnnotations(payload.group_id, payload.book_id,
                payload.chapter_id, JSON.parse(payload.custom_data), payload.revision) : this.booksService.saveViewerConfig(payload);
            const subscription = operation.pipe(first(), timeout(30000)).subscribe(response => {
                if (session !== this.session) { return; }
                if (payload.group_id) {
                    if (!response || Number(response.group_id) !== payload.group_id || Number(response.book_id) !== payload.book_id ||
                        Number(response.chapter_id) !== payload.chapter_id || response.revision !== payload.revision + 1 ||
                        response.can_edit_shared !== true || !response.shared || response.shared.version !== 1 ||
                        readAnnotationData(response.shared).error ||
                        canonicalJson(response.shared) !== canonicalJson(JSON.parse(payload.custom_data))) { failed(); return; }
                    this.queue.forEach(item => { if (this.key(item) === this.key(payload)) { item.revision = response.revision; } });
                    this.sharedRevisions.set(this.key(payload), response.revision);
                } else if (!response || response.error || response.message !== 'Se ha guardado la configuracion.') { failed(); return; }
                if (!payload.group_id && !payload.isGroupMode) { this.afterPersonalSave(payload); }
                this.active = null;
                this.request = null;
                // Iniciar el siguiente antes de publicar un estado sin tareas para no confirmar un flush prematuramente.
                if (this.queue.length && !this.error) { this.sendNext(); } else { this.publish(null); }
            }, failed);
            if (this.active === payload) { this.request = subscription; } else { subscription.unsubscribe(); }
        } catch (_) { failed(); }
    }

    flush(): Observable<boolean> {
        if (this.error) { return of(false); }
        this.sendNext();
        return this.state$.pipe(filter(state => !state.saving), first(), map(state => !state.error && !state.pending));
    }

    beforeLeave(): Observable<boolean> {
        return new Observable<boolean>(observer => {
            const subscriptions = new Subscription();
            const session = this.session;
            const finish = (saved: boolean) => { observer.next(saved); observer.complete(); };
            const drain = () => {
                if (session !== this.session) { finish(false); return; }
                this.editors.forEach((dirty, prepare) => prepare());
                subscriptions.add(this.flush().subscribe(saved => {
                    if (!saved || session !== this.session) { finish(false); return; }
                    // Una edición pudo llegar mientras esperábamos la respuesta HTTP.
                    this.editors.forEach((dirty, prepare) => prepare());
                    if (this.hasPending) { drain(); } else { finish(true); }
                }));
            };
            drain();
            return subscriptions;
        });
    }

    beforeLogout(): Observable<boolean> { return this.beforeLeave(); }

    startSession(): void { this.sessionOpen = true; this.sessionWarning = null; }

    retry(): void {
        if (this.conflict) { return; }
        if (this.tooLarge) {
            // Con el error activo, el editor solo actualiza la cola; no se envía nada todavía.
            this.editors.forEach((dirty, prepare) => prepare());
            if (!this.canRetry) { return; }
            this.tooLarge = null;
        }
        // El editor puede conservar un cambio que no llegó a encolarse por validación o tamaño.
        this.blockedError = null;
        this.publish(null);
        this.editors.forEach((dirty, prepare) => prepare());
        this.sendNext();
    }

    endSession(): void {
        if (this.hasPending || Array.from(this.editors.values()).some(dirty => dirty())) {
            this.sessionWarning = 'La sesión ha terminado con cambios del visor sin confirmar. No se garantiza que se hayan guardado.';
        }
        // Una sesión expirada nunca debe reenviar contenido privado con el token del siguiente usuario.
        this.session++;
        this.sessionOpen = false;
        if (this.request) { this.request.unsubscribe(); }
        this.request = null;
        this.active = null;
        this.queue = [];
        this.blockedError = null;
        this.conflict = false;
        this.tooLarge = null;
        this.expiredSessionWithConflict = false;
        this.forcedExitApproved = false;
        this.sharedRevisions.clear();
        if (this.progressRequest) { this.progressRequest.unsubscribe(); }
        this.progressRequest = null;
        this.progressActive = null;
        this.progressQueue = [];
        this.progressSaved.clear();
        this.progressLatest.clear();
        this.publish(null);
    }

    setSharedRevision(groupId: number, bookId: number, chapterId: number, revision: number): void {
        const key = this.key({ group_id: groupId, book_id: bookId, chapter_id: chapterId, isGroupMode: 2 } as ViewerSavePayload);
        this.sharedRevisions.set(key, revision);
    }

    enqueueShared(groupId: number, bookId: number, chapterId: number, json: string): boolean {
        let shared: any;
        try { shared = JSON.parse(json); } catch (_) { return false; }
        if (!shared || shared.version !== 1 || !Array.isArray(shared.annotations) || readAnnotationData(shared).error ||
            !Number.isInteger(groupId) || groupId < 1 || !Number.isInteger(bookId) || bookId < 1 ||
            !Number.isInteger(chapterId) || chapterId < 1) { return false; }
        const payload: ViewerSavePayload = { group_id: groupId, book_id: bookId, chapter_id: chapterId,
            book_index_id: chapterId, isGroupMode: 2, page_current: 1, custom_data: json };
        const key = this.key(payload);
        const revision = this.sharedRevisions.get(key);
        if (revision === undefined || this.conflict) { return false; }
        payload.revision = revision;
        return this.enqueue(payload);
    }
}
